import { ReportingControllerBaseConfig } from '../../../../base';
import { FieldItemFormValues } from '../item';
import { FieldItemContainerProps } from '../item/fieldItemProps';
import { FieldListController } from './fieldListInterface';
import { FieldListViewProps } from './fieldListProps';

/**
 * Generate a local ID for new form fields/options
 * These are used for form state management and should not be sent to the server
 */
function generateLocalId(): string {
    return `_local_${crypto.randomUUID()}`;
}

export function createFieldListController(
    config: ReportingControllerBaseConfig
): FieldListController {
    const {
        kernel: {
            infra: { formatter },
        },
    } = config;

    return {
        useProps(context, item, props): FieldListViewProps {
            return {
                items: props.values.fields.map(
                    (field, index): Omit<FieldItemContainerProps, 'as'> => {
                        const totalFields = props.values.fields.length;
                        const isFirst = index === 0;
                        const isLast = index === totalFields - 1;

                        return {
                            id: field._local_id, // Use local ID for component key
                            value: field,
                            onChange: (updatedField) => {
                                // Ensure the updatedField preserves the original field's _local_id
                                const fieldWithPreservedId = {
                                    ...updatedField,
                                    _local_id: field._local_id,
                                };
                                props.onUpdate(fieldWithPreservedId);
                            },
                            onRemove: () => {
                                props.onRemove(field);
                            },
                            onReorder: (toIndex: number) => {
                                props.onReorder(field, toIndex);
                            },
                            index,
                            totalFields,
                            isFirst,
                            isLast,
                        };
                    }
                ),
                add: {
                    options: item.type.map((typeOption) => ({
                        id: typeOption.value,
                        label: typeOption.displayName,
                        Icon: typeOption.Icon,
                        onClick() {
                            const newField: FieldItemFormValues = {
                                _local_id: generateLocalId(), // Use local ID for form state management
                                id: undefined, // No server ID for new fields
                                title: null,
                                type: typeOption.value,
                                required: false,
                                description: null,
                                showDescription: false,
                                formula: null,
                                // Initialize default options for enum fields
                                options:
                                    typeOption.value === 'enum'
                                        ? [
                                              {
                                                  _local_id: generateLocalId(),
                                                  id: undefined,
                                                  displayName: 'Option 1',
                                              },
                                              {
                                                  _local_id: generateLocalId(),
                                                  id: undefined,
                                                  displayName: 'Option 2',
                                              },
                                          ]
                                        : null,
                            };
                            props.onAdd(newField, props.values.fields.length);
                        },
                    })),
                },
                onInsert: (fieldType: string, index: number) => {
                    const newField: FieldItemFormValues = {
                        _local_id: generateLocalId(), // Use local ID for form state management
                        id: undefined, // No server ID for new fields
                        title: null,
                        type: fieldType as any, // Cast to FieldType since we know it's valid
                        required: false,
                        description: null,
                        showDescription: false,
                        formula: null,
                        // Initialize default options for enum fields
                        options:
                            fieldType === 'enum'
                                ? [
                                      {
                                          _local_id: generateLocalId(),
                                          id: undefined,
                                          displayName: 'Option 1',
                                      },
                                      {
                                          _local_id: generateLocalId(),
                                          id: undefined,
                                          displayName: 'Option 2',
                                      },
                                  ]
                                : null,
                    };
                    props.onAdd(newField, index);
                },
            };
        },
    };
}
